package com.swetdot.swetgram.ui.groups

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import com.swetdot.swetgram.domain.User
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetAvatar
import com.swetdot.swetgram.ui.design.SwetBlackBadge
import com.swetdot.swetgram.ui.design.TouchTarget
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens

/**
 * A person, a line about them, and one or two actions on the right.
 *
 * ONE ROW SHAPE FOR BOTH MODERATION LISTS -- the join queue and the ban list -- rather than two,
 * which is the whole reason this file exists. They are the same anatomy: somebody's avatar and
 * name, a line saying what happened and when, and a control. Writing it twice is how the two would
 * end up on different grids after the first edit to either.
 *
 * WHY NOT `SwetListRow`. SDS §12.1's row is the 72dp CONVERSATION row and its trailing slot is a
 * timestamp and an unread badge. Both lists here need a trailing ACTION, and forcing one through
 * that slot is how a design system starts lying about what its components mean --
 * `GroupMembersScreen` avoids exactly this by putting its actions in a card instead. A queue is the
 * case where a card does not work: an admin clearing ten requests would face thirty taps.
 *
 * Every measurement is `SwetListRow`'s own -- 72dp minimum, 44dp avatar, 16dp horizontal margin,
 * `titleMedium` over `bodySmall` -- so the three lists sit on the same grid.
 *
 * NO ONLINE DOT. Neither of these people is necessarily a member: an applicant is not in the group
 * yet, and somebody who banned a user may have left since. The server projects presence from a
 * shared-group relationship it has to compute per row, and drawing a grey dot for a withheld value
 * would claim we know they are offline. `GroupMembersScreen` shows the dot because there the
 * relationship is guaranteed; here it is not.
 *
 * TODO(design-system): this is the third caller waiting on a people-row with an action slot to move
 * into `ui/design` -- `BlockedUsersScreen.BlockedUserRow` is the same component with the same note.
 */
@Composable
internal fun GroupPersonRow(
    user: User,
    /** "Asked on 03.08.2026", "Banned by Ada on 01.08.2026". Null when nothing true can be said. */
    detail: String?,
    /** The action or actions. Kept as a slot so one row serves Unban and Approve/Reject alike. */
    trailing: @Composable () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors

    Row(
        modifier = Modifier
            .fillMaxWidth()
            // A minimum, not a height: a large system font grows the row instead of clipping it.
            .defaultMinSize(minHeight = TouchTarget.chatRowHeight)
            .padding(horizontal = Space.two, vertical = Space.one),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SwetAvatar(
            displayName = user.displayName,
            size = Sizes.avatarMedium,
            surfaceColor = colors.background,
            avatarUrl = user.avatarUrl,
        )

        Spacer(Modifier.width(Space.two))

        Column(Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = user.displayName,
                    style = MaterialTheme.typography.titleMedium,
                    color = colors.textPrimary,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f, fill = false),
                )
                if (user.hasBlackBadge) {
                    Spacer(Modifier.width(Space.half))
                    SwetBlackBadge()
                }
            }
            Spacer(Modifier.height(Space.half))
            Text(
                text = "@${user.username}",
                style = MaterialTheme.typography.bodySmall,
                color = colors.textSecondary,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            // Omitted, not faked, when there is nothing true to put here -- an unreadable date.
            if (detail != null) {
                Spacer(Modifier.height(Space.half))
                Text(
                    text = detail,
                    style = MaterialTheme.typography.labelSmall,
                    color = colors.textSecondary,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }

        Spacer(Modifier.width(Space.one))

        trailing()
    }
}
